/**
 * A portal bot's answer: the input of its `deliver` call, live and after reload alike. The
 * portal leads and specialists all deliver `{ summary, figures[], ids[] }`: the summary is the
 * answer (markdown, often with a table), the figures are its audit trail, the ids are for
 * machines. Any other shape renders as its raw JSON rather than vanishing.
 */
import { useMemo } from "react";
import { INTERNAL } from "@myela/ai-core-react/primitives";
import { Disclosure } from "./Disclosure";
import { MarkdownBlock, STREAM_SMOOTH } from "./MessageMarkdown";

const { useSmooth } = INTERNAL;
type SmoothState = Parameters<typeof useSmooth>[0];
const RUNNING = { type: "running" } as const;
const COMPLETE = { type: "complete" } as const;

type Figure = { label: string; value: string };

const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
/** An image or file ref (`@myela/primitives/tools` `ArtifactRef`): never cut, shown whole. */
const isImageRef = (v: unknown) => isObj(v) && typeof v.key === "string";

/** String leaves in key order, the order `deliver`'s input streams in. A file ref is whole. */
function textOf(v: unknown): string {
  if (typeof v === "string") return v;
  if (isImageRef(v)) return "";
  if (Array.isArray(v)) return v.map(textOf).join("");
  return isObj(v) ? Object.values(v).map(textOf).join("") : "";
}

/** `v` with its string leaves cut to the first `left.n` characters overall, in key order. */
function revealed(v: unknown, left: { n: number }): unknown {
  if (typeof v === "string") {
    const shown = v.slice(0, Math.max(0, left.n));
    left.n -= v.length;
    return shown;
  }
  if (isImageRef(v)) return v;
  if (Array.isArray(v)) return v.map((x) => revealed(x, left));
  if (!isObj(v)) return v;
  return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, revealed(x, left)]));
}

/**
 * The report as `deliver`'s input streams: its text types out as a text part's does, so the
 * reply stream's batches read as typing, not jumps. A settled report shows whole at once.
 */
export function StreamingDeliveredReport({ value, running }: { value: unknown; running: boolean }) {
  const text = textOf(value);
  const status: SmoothState["status"] = running ? RUNNING : COMPLETE;
  const { text: shown } = useSmooth({ type: "text", text, status }, STREAM_SMOOTH);
  const view = useMemo(() => revealed(value, { n: shown.length }), [value, shown.length]);
  return <DeliveredReport value={view} />;
}

const figuresOf = (v: unknown): Figure[] =>
  Array.isArray(v)
    ? v.filter(
        (f): f is Figure => isObj(f) && typeof f.label === "string" && typeof f.value === "string",
      )
    : [];

export function DeliveredReport({ value }: { value: unknown }) {
  if (!isObj(value) || typeof value.summary !== "string") {
    return (
      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  const figures = figuresOf(value.figures);
  return (
    <div className="flex flex-col gap-1">
      <MarkdownBlock text={value.summary} />
      {figures.length > 0 && (
        <Disclosure label={<span>Figures ({figures.length})</span>}>
          <dl className="text-xs">
            {figures.map((f, i) => (
              <div
                key={`${f.label}-${i}`}
                className="flex items-baseline justify-between gap-4 border-b border-border/60 py-1.5 last:border-b-0"
              >
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="tabular-nums font-medium text-foreground">{f.value}</dd>
              </div>
            ))}
          </dl>
        </Disclosure>
      )}
    </div>
  );
}
